import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import WorkCard from "@/components/feature/WorkCard";
import WorkFilters from "@/pages/works/components/WorkFilters";
import useSeo from "@/hooks/useSeo";
import { allWorks } from "@/lib/content";

const PAGE_SIZE = 8;

export default function WorksList() {
  useSeo({
    title: "筱田优影视作品集 - 电影与电视剧介绍",
    description: "筱田优影视作品集，收录其主演的电影与电视剧作品，涵盖剧情简介、类型、年份与主创阵容。",
    keywords: "筱田优作品,电影作品,电视剧,影视作品集",
  });

  const [type, setType] = useState("全部");
  const [year, setYear] = useState("全部");
  const [searchParams] = useSearchParams();
  const [tag, setTag] = useState(searchParams.get("tag") || "全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [type, year, tag, sort]);

  const filtered = useMemo(() => {
    const base = allWorks.filter((w) => {
      if (type !== "全部" && w.type !== type) return false;
      if (year !== "全部" && String(w.year) !== year) return false;
      if (tag !== "全部" && !w.tags.includes(tag)) return false;
      return true;
    });
    return [...base].sort((a, b) =>
      sort === "popular" ? b.popularity - a.popularity : b.year - a.year || b.popularity - a.popularity,
    );
  }, [type, year, tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setType("全部");
    setYear("全部");
    setTag("全部");
    setSort("latest");
    setPage(1);
  };

  const activeCount = [type, year, tag].filter((v) => v !== "全部").length;

  return (
    <div className="w-full pt-20 md:pt-24">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: "作品" }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 mt-4">
            筱田优影视作品
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl leading-relaxed">
            从电影到电视剧，系统收录筱田优的每一部参演作品，支持按类型、年份与标签快速筛选。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-12">
        <div className="hidden lg:block mb-9 pb-8 border-b border-background-200">
          <WorkFilters
            type={type}
            year={year}
            tag={tag}
            sort={sort}
            onType={setType}
            onYear={setYear}
            onTag={setTag}
            onSort={setSort}
            onReset={resetFilters}
          />
        </div>

        <div className="lg:hidden flex items-center justify-between gap-3 mb-6">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex items-center gap-2 px-4 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选
            {activeCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-xs flex items-center justify-center">
                {activeCount}
              </span>
            )}
          </button>
          <span className="text-sm text-foreground-500">共 {filtered.length} 部作品</span>
        </div>

        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-film-line"
            title="暂无符合条件的作品"
            description="当前筛选组合下没有找到作品，试试调整筛选条件。"
            onReset={resetFilters}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 inset-x-0 max-h-[85vh] overflow-y-auto bg-background-50 rounded-t-2xl p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <WorkFilters
              type={type}
              year={year}
              tag={tag}
              sort={sort}
              onType={setType}
              onYear={setYear}
              onTag={setTag}
              onSort={setSort}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="w-full h-11 mt-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}
    </div>
  );
}